import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Stethoscope,
  Hospital,
  Network,
  Users,
  CalendarCheck,
  Star,
  ScrollText,
  IndianRupee,
  Activity,
  FileText,
  Plug,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import NotificationBell from "./NotificationBell";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
  { to: "/admin/doctors", label: "Doctors", icon: Stethoscope, testId: "admin-nav-doctors" },
  { to: "/admin/clinics", label: "Clinics", icon: Hospital, testId: "admin-nav-clinics" },
  { to: "/admin/associations", label: "Associations", icon: Network, testId: "admin-nav-associations" },
  { to: "/admin/patients", label: "Patients", icon: Users, testId: "admin-nav-patients" },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarCheck, testId: "admin-nav-appointments" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, testId: "admin-nav-reviews" },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, testId: "admin-nav-audit-logs" },
  { to: "/admin/payments", label: "Payments", icon: IndianRupee, testId: "admin-nav-payments" },
  { to: "/admin/diagnostics", label: "Diagnostics", icon: Activity, testId: "admin-nav-diagnostics" },
  { to: "/admin/integrations", label: "Integrations", icon: Plug, testId: "admin-nav-integrations" },
  {
    label: "Content", icon: FileText, testId: "admin-nav-content",
    children: [
      { to: "/admin/content/categories", label: "Categories", testId: "admin-nav-categories" },
      { to: "/admin/content/banners", label: "Banners", testId: "admin-nav-banners" },
      { to: "/admin/content/blogs", label: "Blogs", testId: "admin-nav-blogs" },
    ],
  },
];

export default function AdminLayout({ children, title, subtitle, action }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("admin_sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(() => location.pathname.startsWith("/admin/content"));

  useEffect(() => {
    localStorage.setItem("admin_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin/content")) setContentOpen(true);
  }, [location.pathname]);

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const SidebarInner = ({ onItem }) => (
    <div className="h-full flex flex-col">
      <div className={"px-4 py-5 flex items-center gap-2 " + (collapsed ? "justify-center" : "justify-between")}>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
              <Stethoscope className="h-5 w-5" />
            </span>
            <span className="font-semibold text-base tracking-tight">
              Healthink<span className="text-primary">+</span>
            </span>
          </div>
        )}
        {collapsed && (
          <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Stethoscope className="h-5 w-5" />
          </span>
        )}
      </div>
      <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
        {NAV.map((n) => {
          if (n.children) {
            const anyActive = n.children.some((c) => location.pathname.startsWith(c.to));
            const open = collapsed ? false : contentOpen || anyActive;
            return (
              <div key={n.label}>
                <button
                  type="button"
                  onClick={() => setContentOpen((v) => !v)}
                  data-testid={n.testId}
                  className={
                    "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors " +
                    (anyActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary") +
                    (collapsed ? " justify-center" : "")
                  }
                >
                  <n.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="flex-1 text-left">{n.label}</span>}
                  {!collapsed && (open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                </button>
                {open && !collapsed && (
                  <div className="ml-7 mt-0.5 space-y-0.5">
                    {n.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        onClick={onItem}
                        data-testid={c.testId}
                        className={({ isActive }) =>
                          "block px-3 py-1.5 rounded-lg text-xs font-medium transition-colors " +
                          (isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary")
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onItem}
              data-testid={n.testId}
              end={n.to === "/admin/dashboard"}
              className={({ isActive }) =>
                "flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors " +
                (isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary") +
                (collapsed ? " justify-center" : "")
              }
            >
              <n.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-2 border-t border-border">
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-sidebar-logout"
          className={
            "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors " +
            (collapsed ? "justify-center" : "")
          }
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        data-testid="admin-sidebar-toggle"
        className="hidden lg:flex absolute -right-3 top-16 h-6 w-6 rounded-full bg-white border border-border shadow-sm items-center justify-center hover:bg-secondary"
        aria-label="Toggle sidebar"
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-card text-foreground flex">
      {/* Desktop sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={
          "hidden lg:flex bg-white border-r border-border relative transition-[width] duration-200 " +
          (collapsed ? "w-16" : "w-60")
        }
      >
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white border-r border-border">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute -right-3 top-4 h-7 w-7 rounded-full bg-white border border-border flex items-center justify-center"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </button>
            <SidebarInner onItem={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white/80 backdrop-blur flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              data-testid="admin-mobile-menu-btn"
              className="lg:hidden h-9 w-9 rounded-full hover:bg-secondary flex items-center justify-center"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              {title && (
                <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate">{title}</h1>
              )}
              {subtitle && (
                <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {action}
            {user && <NotificationBell />}
            <div
              data-testid="admin-header-user"
              className="hidden sm:flex h-9 w-9 rounded-full bg-accent text-accent-foreground items-center justify-center text-sm font-semibold"
              title={user?.name || user?.email}
            >
              {(user?.name || "A").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
